import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Paper,
  LinearProgress,
  Alert,
  Skeleton,
  TextField,
  MenuItem,
} from '@mui/material'
import { FC, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import ViewWeekIcon from '@mui/icons-material/ViewWeek'
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet'
import BookmarkAddedIcon from '@mui/icons-material/BookmarkAdded'
import WarningIcon from '@mui/icons-material/Warning'
import CampaignIcon from '@mui/icons-material/Campaign'
import AddIcon from '@mui/icons-material/Add'
import { PageHeader } from '@/components/common/PageHeader'
import { dashboardService } from '@/services/dashboardService'
import { DashboardResumo } from '@/types/dashboard'
import { notificacaoService } from '@/services/notificacaoService'
import { NotificacaoResumo } from '@/types/notificacao'
import { metaPara } from './alertasMeta'
import {
  FASES_JORNADA, corDaFase, totalDaFase, valorDaFase,
  ETAPAS_COMERCIAIS, ETAPA_COMERCIAL_LABEL, ETAPA_COMERCIAL_COR,
  CANAIS_COMERCIAIS, CANAL_LABEL,
} from '@/types/statusReserva'
import { authStore } from '@/store/authStore'
import { canalStore } from '@/store/canalStore'
import { tokens } from '@/theme'

// Legenda de cada fase. A fase de Estruturação/Parceiro mostra o valor em jogo no lugar
// da legenda — é o número que o consultor procura ali.
const SUB_FASE: Record<string, string> = {
  'Cadastro': 'Aguardando envio',
  'Análise Originei': 'Em análise interna',
  'Estruturação / Parceiro': 'Em estruturação',
  'Contratação / Operação': 'Fechadas',
  'Encerramento': 'Encerradas',
}

const formatBRL = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0)


const DashboardLegado: FC = () => {
  const navigate = useNavigate()
  const [resumo, setResumo] = useState<DashboardResumo | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [alertas, setAlertas] = useState<NotificacaoResumo[]>([])
  const [carregandoAlertas, setCarregandoAlertas] = useState(true)
  // Recorte por canal (6.7) — comparação por canal é do back-office; o consultor não vê
  // o seletor (e o backend ignoraria o parâmetro de qualquer forma).
  const [canal, setCanal] = useState<string>('')
  // Onda 1 (A02): o seletor global da topbar (ADMIN/GERENTE) comanda o recorte;
  // o seletor local continua para refinar (inclui "Sem canal").
  const canalGlobal = canalStore((st) => st.canal)
  useEffect(() => { setCanal(canalGlobal) }, [canalGlobal])
  const usuarioLogado = authStore((st) => st.usuario)
  const podeCompararCanais = !!usuarioLogado?.permissoes?.some((p) =>
    ['ADMIN', 'GERENTE', 'OPERADOR'].includes(p.toUpperCase()))

  useEffect(() => {
    let ativo = true
    setCarregando(true)
    dashboardService
      .getResumo(canal || undefined)
      .then((data) => { if (ativo) setResumo(data) })
      .catch((e) => { if (ativo) setErro(e?.message ?? 'Falha ao carregar resumo') })
      .finally(() => { if (ativo) setCarregando(false) })
    return () => { ativo = false }
  }, [canal])

  useEffect(() => {
    let ativo = true
    setCarregandoAlertas(true)
    notificacaoService
      .recentes(5)
      .then((lista) => { if (ativo) setAlertas(lista) })
      .catch(() => { if (ativo) setAlertas([]) })
      .finally(() => { if (ativo) setCarregandoAlertas(false) })
    return () => { ativo = false }
  }, [])

  const porStatus = resumo?.pipeline?.porStatus ?? {}
  const financeiro = resumo?.financeiro
  const meta = resumo?.meta
  const progressoMeta = meta && meta.trimestre > 0 ? Math.min(100, (meta.fechado / meta.trimestre) * 100) : 0

  return (
    <Box>
      <PageHeader
        title="Dashboard"
        subtitle="Visão geral do seu pipeline, reservas e financeiro"
        action={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/reservas/nova')}>
            Nova Reserva
          </Button>
        }
      />

      {erro && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Não foi possível carregar o resumo do dashboard. {erro}
        </Alert>
      )}

      {/* PIPELINE */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Typography variant="overline" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700, color: tokens.textSecondary }}>
            <ViewWeekIcon sx={{ fontSize: 18, color: tokens.brandPrimary }} /> Pipeline
          </Typography>
          {/* Comparação por canal (6.7) — só back-office. "Sem canal" = reservas antigas,
              anteriores ao carimbo; escondê-las faria volume sumir da comparação. */}
          {podeCompararCanais && (
            <TextField
              select
              size="small"
              label="Canal"
              value={canal}
              onChange={(e) => setCanal(e.target.value)}
              sx={{ minWidth: 240 }}
              inputProps={{ 'aria-label': 'Filtrar por canal comercial' }}
            >
              <MenuItem value="">Todos os canais</MenuItem>
              {CANAIS_COMERCIAIS.map((c) => (
                <MenuItem key={c} value={c}>{CANAL_LABEL[c]}</MenuItem>
              ))}
              <MenuItem value="SEM_CANAL">Sem canal (reservas antigas)</MenuItem>
            </TextField>
          )}
        </Box>
        {/* Funil em seis etapas comerciais (decisão 6.4): Reserva → Reunião → Estruturação →
            Negociação → Ganho → Perdido, derivadas no backend (Reunião depende do primeiro
            contato). Se o backend ainda não mandar `porEtapaComercial`, cai nas cinco fases
            da jornada — mesmo card, outra chave. */}
        <Grid container spacing={2}>
          {resumo?.pipeline.porEtapaComercial
            ? ETAPAS_COMERCIAIS.map((etapa) => (
                <Grid item xs={6} sm={4} md={2} key={etapa}>
                  <Card
                    sx={{ cursor: 'pointer', borderLeft: `3px solid ${ETAPA_COMERCIAL_COR[etapa]}`, '&:hover': { boxShadow: 4 } }}
                    onClick={() => navigate('/kanban')}
                  >
                    <CardContent sx={{ p: '16px !important' }}>
                      <Typography variant="caption" sx={{ color: tokens.textSecondary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        {ETAPA_COMERCIAL_LABEL[etapa]}
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 700, my: 0.5 }}>
                        {carregando ? <Skeleton width={40} /> : (resumo.pipeline.porEtapaComercial?.[etapa] ?? 0)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: tokens.statusSuccess, fontWeight: 500, display: 'block' }}>
                        {formatBRL(Number(resumo.pipeline.valorPorEtapaComercial?.[etapa] ?? 0))}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))
            : FASES_JORNADA.map((fase) => (
                <Grid item xs={12} sm={6} md={4} key={fase}>
                  <Card
                    sx={{ cursor: 'pointer', borderLeft: `3px solid ${corDaFase(fase)}`, '&:hover': { boxShadow: 4 } }}
                    onClick={() => navigate('/kanban')}
                  >
                    <CardContent sx={{ p: '20px !important' }}>
                      <Typography variant="caption" sx={{ color: tokens.textSecondary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        {fase}
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 700, my: 0.5 }}>
                        {carregando ? <Skeleton width={40} /> : totalDaFase(porStatus, fase)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: tokens.statusSuccess, fontWeight: 500, display: 'block' }}>
                        {carregando || !resumo
                          ? <Skeleton width={90} />
                          : formatBRL(valorDaFase(resumo.pipeline.valorPorStatus, fase))}
                      </Typography>
                      <Typography variant="caption" sx={{ color: tokens.textSecondary }}>
                        {SUB_FASE[fase] ?? ''}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
        </Grid>
      </Box>

      {/* FINANCEIRO + RESERVAS */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: '20px !important' }}>
              <Typography variant="overline" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, fontWeight: 700, color: tokens.textSecondary }}>
                <AccountBalanceWalletIcon sx={{ fontSize: 18, color: tokens.brandPrimary }} /> Financeiro
              </Typography>
              {[
                { label: 'Volume em negociação', value: financeiro?.volumeEmNegociacao },
                { label: 'Volume fechado',       value: financeiro?.volumeFechado, color: tokens.statusSuccess },
                { label: 'Comissão projetada',        value: financeiro?.comissaoProjetada, color: tokens.brandPrimary },
                { label: 'Disponível para nota fiscal', value: financeiro?.comissaoDisponivelParaNota, color: tokens.statusSuccess, bold: true },
              ].map((item, i, arr) => (
                <Box key={item.label} sx={{ display: 'flex', justifyContent: 'space-between', py: 1.25, borderBottom: i < arr.length - 1 ? '1px solid ' + tokens.divider : 'none' }}>
                  <Typography variant="body2" color="text.secondary">{item.label}</Typography>
                  <Typography variant="body2" sx={{ fontWeight: item.bold ? 700 : 500, color: item.color || '#111111' }}>
                    {carregando ? <Skeleton width={100} /> : formatBRL(item.value ?? 0)}
                  </Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: '20px !important' }}>
              <Typography variant="overline" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, fontWeight: 700, color: tokens.textSecondary }}>
                <BookmarkAddedIcon sx={{ fontSize: 18, color: tokens.brandPrimary }} /> Reservas
              </Typography>
              <Grid container spacing={1.5} sx={{ mb: 2 }}>
                {[
                  {
                    value: totalDaFase(porStatus, 'Cadastro') + totalDaFase(porStatus, 'Análise Originei'),
                    label: 'Em análise', bg: tokens.brandPrimarySoft, color: tokens.brandPrimary,
                  },
                  { value: totalDaFase(porStatus, 'Estruturação / Parceiro'), label: 'Em estruturação', bg: '#FEF9E7', color: tokens.statusWarning },
                  { value: totalDaFase(porStatus, 'Contratação / Operação'), label: 'Contratadas', bg: '#E8F8F0', color: tokens.statusSuccess },
                ].map((item) => (
                  <Grid item xs={4} key={item.label}>
                    <Paper sx={{ textAlign: 'center', p: 2, bgcolor: item.bg, boxShadow: 'none' }}>
                      <Typography variant="h4" fontWeight={700} color={item.color}>
                        {carregando ? <Skeleton width={30} sx={{ mx: 'auto' }} /> : item.value}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{item.label}</Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
              <Button variant="outlined" fullWidth onClick={() => navigate('/reservas')}>
                Ver todas as reservas
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ALERTAS */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="overline" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, fontWeight: 700, color: tokens.textSecondary }}>
          <WarningIcon sx={{ fontSize: 18, color: tokens.brandPrimary }} /> Alertas
        </Typography>
        {carregandoAlertas ? (
          [0, 1, 2].map((i) => (
            <Skeleton key={i} variant="rectangular" height={52} sx={{ borderRadius: 1, mb: 1 }} />
          ))
        ) : alertas.length === 0 ? (
          <Alert severity="info" sx={{ mb: 1 }}>
            Nenhum alerta no momento.
          </Alert>
        ) : (
          alertas.map((n) => {
            const meta = metaPara(n.evento)
            return (
              <Alert
                key={n.id}
                severity={meta.severity}
                icon={meta.icon}
                sx={{ mb: 1, alignItems: 'center' }}
                action={
                  <Button color="inherit" size="small" onClick={() => navigate(meta.action)}>
                    Ver
                  </Button>
                }
              >
                {n.titulo}
              </Alert>
            )
          })
        )}
      </Box>

      {/* CAMPANHAS */}
      <Box>
        <Typography variant="overline" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, fontWeight: 700, color: tokens.textSecondary }}>
          <CampaignIcon sx={{ fontSize: 18, color: tokens.brandPrimary }} /> Campanhas
        </Typography>
        <Paper
          sx={{
            p: 3,
            background: 'linear-gradient(135deg, ' + tokens.brandPrimary + ', ' + tokens.brandPrimaryLight + ')',
            color: '#fff',
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            {meta?.nome ?? 'Meta do Trimestre'}: {meta ? formatBRL(meta.trimestre) : <Skeleton width={140} sx={{ bgcolor: 'rgba(255,255,255,0.3)' }} />}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9, mb: 2 }}>
            {meta ? (
              <>Volume fechado: {formatBRL(meta.fechado)} ({progressoMeta.toFixed(0)}%) — Faltam {formatBRL(Math.max(0, meta.trimestre - meta.fechado))} para atingir a meta!</>
            ) : (
              <Skeleton width={320} sx={{ bgcolor: 'rgba(255,255,255,0.3)' }} />
            )}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={progressoMeta}
            sx={{
              height: 8,
              borderRadius: 4,
              bgcolor: 'rgba(255,255,255,0.2)',
              '& .MuiLinearProgress-bar': { bgcolor: '#fff', borderRadius: 4 },
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, fontSize: '0.75rem', opacity: 0.8 }}>
            <span>{meta ? formatBRL(meta.fechado) : '—'}</span>
            <span>{meta ? formatBRL(meta.trimestre) : '—'}</span>
          </Box>
        </Paper>
      </Box>
    </Box>
  )
}

export default DashboardLegado
